// ─── ─ SlidebookerLinks ──────────────────────────────────────────────────────
//
// Ouvre en lightbox VelWidget les liens pointant vers *.slidebooker.com,
// sans toucher aux blocs Gutenberg / options ACF (champ "lien" uniquement).
//
// - Délégation d'événement : couvre aussi le contenu injecté après coup
// - Dégradation propre : si VelWidget est absent, le lien reste un lien
// - Ctrl / Cmd / clic milieu préservés (ouverture dans un nouvel onglet)
//
// Opt-out   : data-hwc-slidebooker-ignore sur le lien ou un de ses parents
// Surcharge : data-sb-activity / data-sb-age / data-sb-type / data-sb-vendor…
// ─────────────────────────────────────────────────────────────────────────────

export default class SlidebookerLinks {

	constructor(options = {}) {

		this.selector = 'a[href*="slidebooker.com"]';
		this.ignore   = '[data-hwc-slidebooker-ignore]';

		this.config = Object.assign({
			env           : null,   // 'staging' | 'production' — doit matcher le vel.js chargé
			locale        : null,
			vendorSlug    : null,
			groupSlug     : null,   // uniquement en vente "groupe" (site national)
			resetCriteria : true,   // ouvre une recherche vierge (cf. _buildParams)
		}, window.hwcSlidebooker || {}, options);

		this._onClick = this._onClick.bind(this);

		document.addEventListener('click', this._onClick);
	}

	destroy() {
		document.removeEventListener('click', this._onClick);
	}

	// ─── ─ Handler

	_onClick(event) {

		if (event.defaultPrevented) return;

		// on laisse passer les "ouvrir dans un nouvel onglet"
		if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

		const link = event.target.closest(this.selector);
		if (!link) return;
		if (link.closest(this.ignore)) return;
		if (link.hasAttribute('onclick')) return;   // déjà piloté par un widget

		if (!this._widgetIsReady()) return;         // navigation classique

		const params = this._buildParams(link);
		if (!params || !params.vendorSlug) return;

		event.preventDefault();

		try {
			window.VelWidget.openSearch(params);
		} catch (error) {
			console.warn('[SlidebookerLinks] openSearch a échoué, fallback navigation.', error);
			window.location.href = link.href;
		}
	}

	_widgetIsReady() {
		return typeof window.VelWidget !== 'undefined'
			&& typeof window.VelWidget.openSearch === 'function';
	}

	// ─── ─ Paramètres

	_buildParams(link) {

		const parsed = this._parseUrl(link.href);
		if (!parsed) return null;

		const params = Object.assign({}, parsed);

		// l'URL est prioritaire pour l'école et la langue, la conf sert de repli
		['locale', 'vendorSlug', 'groupSlug'].forEach((key) => {
			if (!params[key] && this.config[key]) params[key] = this.config[key];
		});

		// env : la conf du thème fait toujours foi, elle seule est alignée
		// sur le vel.js réellement chargé (doc : script et env doivent matcher)
		if (this.config.env) params.env = this.config.env;

		// surcharges portées par le lien
		const map = {
			sbEnv      : 'env',
			sbLocale   : 'locale',
			sbVendor   : 'vendorSlug',
			sbActivity : 'activity',
			sbAge      : 'age',
			sbType     : 'type',
		};

		Object.keys(map).forEach((dataKey) => {
			if (link.dataset[dataKey]) params[map[dataKey]] = link.dataset[dataKey];
		});

		// sans ces valeurs, openSearch() hérite des critères du dernier init()
		// présent sur la page (fiche activité) et s'ouvrirait pré-filtré
		if (this.config.resetCriteria !== false) {
			if (!('activity' in params)) params.activity = '';
			if (!('age'      in params)) params.age      = 'tous';
			if (!('type'     in params)) params.type     = 'tous';
		}

		params.locale = (params.locale || document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();

		return params;
	}

	// ex. https://prod.slidebooker.com/entry/#/vel/fr/g/v/esi-pro-7-les-7-laux
	_parseUrl(href) {

		let url;

		try {
			url = new URL(href, window.location.href);
		} catch (error) {
			return null;
		}

		if (!/(^|\.)slidebooker\.com$/i.test(url.hostname)) return null;

		const params = {
			env : /^dev\./i.test(url.hostname) ? 'staging' : 'production',
		};

		const segments = url.hash.replace(/^#\/?/, '').split('/').filter(Boolean);
		const start    = segments.indexOf('vel');

		if (start === -1) return params;

		const rest = segments.slice(start + 1);

		// premier segment après "vel" = locale
		if (rest.length && /^[a-z]{2}(-[a-z]{2})?$/i.test(rest[0])) {
			params.locale = rest.shift().slice(0, 2).toLowerCase();
		}

		// on ne retient que le vendeur : groupSlug bascule la vente sur le canal
		// marketplace du groupe, il ne doit venir que d'une conf explicite
		const keys = { v : 'vendorSlug' };

		for (let i = 0; i < rest.length; i++) {

			const key = keys[rest[i]];
			if (!key) continue;

			const value = rest[i + 1];

			if (value && !keys[value]) {
				params[key] = value;
				i++;
			}
		}

		return params;
	}
}